import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Sidebar } from '../components/Sidebar';
import { EmailList } from '../components/EmailList';
import { EmailView } from '../components/EmailView';
import { ComposeModal } from '../components/ComposeModal';
import { ContactsView } from '../components/ContactsView';
import { SettingsView } from '../components/SettingsView';
import { toast } from 'sonner';
import { Menu, Search, ChevronDown, Pencil, RefreshCw } from 'lucide-react';
import { cn } from '../lib/utils';
import * as authStorage from '../lib/authStorage';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;
const DEFAULT_PAGE_SIZE = 50;
const getPageSize = () => parseInt(localStorage.getItem('renoviamail_page_size') || DEFAULT_PAGE_SIZE, 10);

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

const DashboardPage = () => {
  const { user, activeAccount, accounts, setActiveAccount } = useAuth();
  const [pageSize, setPageSizeState] = useState(getPageSize);
  const pageSizeRef = useRef(pageSize);
  pageSizeRef.current = pageSize;
  const handlePageSizeChange = useCallback((size) => {
    localStorage.setItem('renoviamail_page_size', size);
    setPageSizeState(size);
  }, []);
  const [folders, setFolders] = useState([]);
  const [labels, setLabels] = useState([]);
  const [emails, setEmails] = useState([]);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [currentView, setCurrentView] = useState('inbox');
  const [isComposeOpen, setIsComposeOpen] = useState(false);
  const [composeData, setComposeData] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [stats, setStats] = useState({ total_emails: 0, unread_emails: 0, starred_emails: 0, important_emails: 0, total_contacts: 0 });
  const [accountsStats, setAccountsStats] = useState({});
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [mobileView, setMobileView] = useState('list');
  const [showFolderDropdown, setShowFolderDropdown] = useState(false);
  const [showAccountDropdown, setShowAccountDropdown] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const listContainerRef = useRef(null);
  const listScrollTopRef = useRef(0);
  const pageCacheRef = useRef(new Map());
  const pageRequestRef = useRef(new Map());
  const pagingInFlightRef = useRef(false);
  const searchDebounceRef = useRef(null);

  const buildPageCacheKey = useCallback((folderId, view, page, accountEmail) => {
    const accountKey = accountEmail || 'primary';
    const folderKey = folderId || 'none';
    return `${accountKey}|${view}|${folderKey}|${page}|${pageSizeRef.current}`;
  }, []);

  const selectedFolderRef = useRef(selectedFolder);
  selectedFolderRef.current = selectedFolder;
  const activeAccountRef = useRef(activeAccount);
  activeAccountRef.current = activeAccount;
  const fetchFolders = useCallback(async (accountEmail = null, resetSelection = false) => {
    try {
      const headers = accountEmail ? { 'X-Account-Email': accountEmail } : {};
      const response = await axios.get(`${API}/folders`, { headers });
      setFolders(response.data);
      if ((resetSelection || !selectedFolderRef.current) && response.data.length > 0) {
        const inbox = response.data.find(f => f.id === 'INBOX' || f.id.toUpperCase() === 'INBOX');
        setSelectedFolder(inbox || response.data[0]);
      }
    } catch (error) {
      console.error('Failed to fetch folders:', error);
      if (error.response?.status === 403) {
        toast.error(error.response?.data?.detail || 'Connexion IMAP refusée');
      }
    }
  }, []);

  const fetchLabels = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/labels`);
      setLabels(response.data);
    } catch (error) {
      console.error('Failed to fetch labels:', error);
    }
  }, []);

  const cacheWindowPages = useCallback((folderId, view, startPage, accountEmail, rows, totalCount) => {
    const size = pageSizeRef.current;
    const pageCount = Math.ceil(rows.length / size);
    for (let i = 0; i < pageCount; i++) {
      const page = startPage + i;
      const slice = rows.slice(i * size, (i + 1) * size);
      if (!slice.length) continue;
      const key = buildPageCacheKey(folderId, view, page, accountEmail);
      pageCacheRef.current.set(key, { emails: slice, totalCount });
    }
  }, [buildPageCacheKey]);

  const warmPageWindow = useCallback(async (folderId, view, page, accountEmail = null, force = false) => {
    if (page < 1) return;
    const firstKey = buildPageCacheKey(folderId, view, page, accountEmail);
    const nextKey = buildPageCacheKey(folderId, view, page + 1, accountEmail);
    if (!force && pageCacheRef.current.has(firstKey) && pageCacheRef.current.has(nextKey)) return;
    if (force) {
      pageCacheRef.current.delete(firstKey);
      pageCacheRef.current.delete(nextKey);
    }

    const size = pageSizeRef.current;
    const windowLimit = Math.min(size * 2, 100);
    const params = { limit: windowLimit, skip: (page - 1) * size };
    if (folderId) params.folder_id = folderId;
    if (view === 'starred') params.is_starred = true;
    if (view === 'important') params.is_important = true;
    const headers = accountEmail ? { 'X-Account-Email': accountEmail } : {};
    const requestKey = `${firstKey}|window:${windowLimit}${force ? ':force' : ''}`;
    if (pageRequestRef.current.has(requestKey)) {
      await pageRequestRef.current.get(requestKey);
      return;
    }
    const req = axios.get(`${API}/emails`, { params, headers })
      .then((res) => {
        const total = Number.parseInt(res.headers['x-total-count'] || '0', 10);
        const rows = res.data || [];
        cacheWindowPages(folderId, view, page, accountEmail, rows, total);
        if (!rows.length) {
          pageCacheRef.current.set(firstKey, { emails: [], totalCount: total });
        }
        return true;
      })
      .catch((error) => {
        console.error('Failed to warm email page:', error);
        return error;
      })
      .finally(() => pageRequestRef.current.delete(requestKey));
    pageRequestRef.current.set(requestKey, req);
    return await req;
  }, [buildPageCacheKey, cacheWindowPages]);

  const fetchEmails = useCallback(async (folderId, view, page = 1, accountEmail = null, silent = false, force = false) => {
    if (!silent) setLoading(true);
    pagingInFlightRef.current = true;
    try {
      const currentKey = buildPageCacheKey(folderId, view, page, accountEmail);
      const cachedNow = pageCacheRef.current.get(currentKey);
      if (cachedNow && !force) {
        setEmails(cachedNow.emails);
        setTotalCount(cachedNow.totalCount);
        setCurrentPage(page);
        if (!silent) setLoading(false);
        warmPageWindow(folderId, view, page + 1, accountEmail);
        return;
      }
      const warmResult = await warmPageWindow(folderId, view, page, accountEmail, force);
      if (warmResult instanceof Error) {
        const message = warmResult.response?.data?.detail || 'Impossible de charger les messages';
        if (!silent) toast.error(message);
      }
      const cachedAfterWarm = pageCacheRef.current.get(currentKey);
      if (cachedAfterWarm) {
        setEmails(cachedAfterWarm.emails);
        setTotalCount(cachedAfterWarm.totalCount);
        setCurrentPage(page);
        if (!silent) setLoading(false);
        warmPageWindow(folderId, view, page + 1, accountEmail);
        return;
      }
    } catch (error) {
      console.error('Failed to fetch emails:', error);
    } finally {
      pagingInFlightRef.current = false;
      if (!silent) setLoading(false);
    }
  }, [buildPageCacheKey, warmPageWindow]);

  const fetchStats = useCallback(async (accountEmail = null) => {
    try {
      const headers = accountEmail ? { 'X-Account-Email': accountEmail } : {};
      const response = await axios.get(`${API}/stats`, { headers });
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  }, []);

  const fetchAllAccountsStats = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/accounts/unread`);
      // Convertir {email: count} en {email: {unread_emails: count}}
      const results = {};
      Object.entries(res.data).forEach(([email, count]) => {
        results[email] = { unread_emails: count };
      });
      setAccountsStats(results);
    } catch { /* silencieux */ }
  }, []);

  const handlePageChange = useCallback((page) => {
    const folderId = (currentView === 'inbox' && selectedFolder) ? selectedFolder.id : null;
    const cacheKey = buildPageCacheKey(folderId, currentView, page, activeAccount);
    const cached = pageCacheRef.current.get(cacheKey);
    setCurrentPage(page);
    if (cached) {
      setEmails(cached.emails);
      setTotalCount(cached.totalCount);
      warmPageWindow(folderId, currentView, page + 1, activeAccount);
    } else {
      // Pagination fluide: pas de skeleton global, on garde la page actuelle affichée
      // pendant le fetch de la prochaine page.
      fetchEmails(folderId, currentView, page, activeAccount, true);
    }
    setSelectedEmail(null);
  }, [currentView, selectedFolder, fetchEmails, activeAccount, buildPageCacheKey, warmPageWindow]);

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    try {
      pageCacheRef.current.clear();
      await fetchFolders(activeAccount);
      const folderId = (currentView === 'inbox' && selectedFolder) ? selectedFolder.id : null;
      await fetchEmails(folderId, currentView, currentPage, activeAccount, false, true);
      await fetchStats(activeAccount);
      toast.success('Actualisé');
    } catch (e) {
      toast.error('Erreur lors de l\'actualisation');
    } finally {
      setRefreshing(false);
    }
  }, [currentView, selectedFolder, currentPage, fetchFolders, fetchEmails, fetchStats, activeAccount]);

  // Badge PWA : met à jour le compteur non lu sur l'icône de l'app
  useEffect(() => {
    const unread = stats.unread_emails || 0;
    if ('setAppBadge' in navigator) {
      unread > 0 ? navigator.setAppBadge(unread) : navigator.clearAppBadge();
    }
    if (navigator.serviceWorker?.controller) {
      navigator.serviceWorker.controller.postMessage({ type: 'SET_BADGE', count: unread });
    }
    document.title = unread > 0 ? `(${unread}) RenoviaMail` : 'RenoviaMail';
  }, [stats.unread_emails]);

  const currentPageRef = useRef(currentPage);
  currentPageRef.current = currentPage;
  const currentViewRef = useRef(currentView);
  currentViewRef.current = currentView;

  const silentRefresh = useCallback(() => {
    if (pagingInFlightRef.current) return;
    const a = activeAccountRef.current;
    const folder = selectedFolderRef.current;
    const view = currentViewRef.current;
    pageCacheRef.current.clear();
    fetchStats(a);
    fetchFolders(a);
    if (view === 'inbox' && folder) {
      fetchEmails(folder.id, 'inbox', currentPageRef.current, a, true, true);
    } else if (view === 'starred' || view === 'important') {
      fetchEmails(null, view, currentPageRef.current, a, true, true);
    }
  }, [fetchStats, fetchFolders, fetchEmails]);

  useEffect(() => {
    const acc = activeAccountRef.current;
    fetchFolders(acc);
    fetchLabels();
    fetchStats(acc);
    const interval = setInterval(silentRefresh, 60000);
    return () => clearInterval(interval);
  }, [fetchFolders, fetchLabels, fetchStats, silentRefresh]);

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') silentRefresh();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [silentRefresh]);

  useEffect(() => {
    if (!navigator.serviceWorker) return;
    const handler = (event) => {
      if (event.data?.type === 'NEW_MAIL') silentRefresh();
    };
    navigator.serviceWorker.addEventListener('message', handler);
    return () => navigator.serviceWorker.removeEventListener('message', handler);
  }, [silentRefresh]);

  useEffect(() => {
    if (!user) return;
    let es;
    let retryTimeout;
    let cancelled = false;
    const connect = async () => {
      try {
        const { data } = await axios.post(`${API}/events-ticket`);
        if (cancelled || !data?.ticket) return;
        es = new EventSource(`${API}/events?ticket=${encodeURIComponent(data.ticket)}`);
        es.addEventListener('new_mail', () => silentRefresh());
        es.onerror = () => {
          es.close();
          if (!cancelled) retryTimeout = setTimeout(connect, 5000);
        };
      } catch {
        if (!cancelled) retryTimeout = setTimeout(connect, 5000);
      }
    };
    connect();
    return () => {
      cancelled = true;
      if (es) {
        es.close();
      }
      if (retryTimeout) clearTimeout(retryTimeout);
    };
  }, [user, silentRefresh]);

  useEffect(() => {
    if (accounts.length > 0) {
      fetchAllAccountsStats();
      const interval = setInterval(fetchAllAccountsStats, 90000);
      return () => clearInterval(interval);
    }
  }, [accounts, fetchAllAccountsStats]);

  // Demander permission notifications
  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, []);

  // Ouvrir l'email directement depuis une notification push
  useEffect(() => {
    if (!navigator.serviceWorker) return;
    const handler = async (event) => {
      if (event.data?.type !== 'OPEN_EMAIL') return;
      const url = event.data.url || '';
      const params = new URLSearchParams(url.includes('?') ? url.slice(url.indexOf('?')) : '');
      const emailId = params.get('email');
      const account = params.get('account');
      if (!emailId) return;
      const [folderId, uid] = emailId.split(':');
      if (!folderId || !uid) return;
      try {
        if (account && account !== activeAccountRef.current) {
          setActiveAccount(account);
        }
        const headers = account ? { 'X-Account-Email': account } : {};
        const res = await axios.get(`${API}/emails`, { params: { folder_id: folderId, limit: 100 }, headers });
        const found = res.data.find(e => e.id === emailId);
        if (found) {
          setSelectedFolder({ id: folderId, name: folderId });
          setCurrentView('inbox');
          handleEmailSelect(found);
        }
      } catch (e) { console.debug('OPEN_EMAIL error:', e); }
    };
    navigator.serviceWorker.addEventListener('message', handler);
    return () => navigator.serviceWorker.removeEventListener('message', handler);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Abonnement Web Push pour notifications (iPhone / PWA en arrière-plan)
  const pushSubscribedRef = useRef(false);
  useEffect(() => {
    if (!user || pushSubscribedRef.current) return;
    if (!('Notification' in window) || Notification.permission !== 'granted') return;
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) return;

    const subscribe = async () => {
      try {
        const { data } = await axios.get(`${API}/push-vapid-public`).catch(() => ({}));
        if (!data?.publicKey) return;
        const reg = await navigator.serviceWorker.ready;
        const key = urlBase64ToUint8Array(data.publicKey);
        const sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: key,
        });
        const json = sub.toJSON();
        await axios.post(`${API}/push-subscription`, {
          endpoint: json.endpoint,
          keys: { p256dh: json.keys?.p256dh, auth: json.keys?.auth },
        });
        pushSubscribedRef.current = true;
      } catch (e) {
        if (e?.response?.status !== 503) console.debug('Push subscribe:', e);
      }
    };
    const t = setTimeout(subscribe, 2000);
    return () => clearTimeout(t);
  }, [user]);

  useEffect(() => {
    if (currentView === 'inbox' && selectedFolder) {
      fetchEmails(selectedFolder.id, 'inbox', 1, activeAccount);
    } else if (currentView === 'starred' || currentView === 'important') {
      fetchEmails(null, currentView, 1, activeAccount);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFolder, currentView, activeAccount]);

  // Recharger tout quand on change de compte
  useEffect(() => {
    pageCacheRef.current.clear();
    pageRequestRef.current.clear();
    setSelectedEmail(null);
    setSelectedFolder(null);
    setCurrentView('inbox');
    setEmails([]);
    setSearchQuery('');
    setSearchResults([]);
    setIsSearching(false);
    setCurrentPage(1);
    setTotalCount(0);
    setMobileView('list');
    setLoading(true); // Afficher le chargement immédiatement
    fetchFolders(activeAccount, true);
    fetchStats(activeAccount);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeAccount]);

  const handleFolderSelect = (folder) => {
    setSelectedFolder(folder);
    setSelectedEmail(null);
    setCurrentView('inbox');
    setSearchQuery('');
    setSearchResults([]);
    setIsMobileSidebarOpen(false);
    setMobileView('list');
    setShowFolderDropdown(false);
  };

  const handleEmailSelect = async (email) => {
    if (listContainerRef.current) {
      listScrollTopRef.current = listContainerRef.current.scrollTop;
    }
    setSelectedEmail(email);
    setMobileView('detail');

    const config = activeAccount ? { headers: { 'X-Account-Email': activeAccount } } : {};
    try {
      const { data } = await axios.get(`${API}/emails/${encodeURIComponent(email.id)}`, config);
      setSelectedEmail(data);
    } catch (error) {
      console.error('Failed to load email details:', error);
    }
    
    if (!email.is_read) {
      try {
        await axios.patch(`${API}/emails/${email.id}`, { is_read: true }, config);
        setEmails(prev => prev.map(e => e.id === email.id ? { ...e, is_read: true } : e));
        fetchFolders(activeAccount);
        fetchStats(activeAccount);
      } catch (error) {
        console.error('Failed to mark as read:', error);
      }
    }
  };

  const handleBackToList = () => {
    setMobileView('list');
    setSelectedEmail(null);
    requestAnimationFrame(() => {
      if (listContainerRef.current) {
        listContainerRef.current.scrollTop = listScrollTopRef.current || 0;
      }
    });
  };

  const handleStarEmail = async (emailId, isStarred) => {
    try {
      const config = activeAccount ? { headers: { 'X-Account-Email': activeAccount } } : {};
      await axios.patch(`${API}/emails/${emailId}`, { is_starred: isStarred }, config);
      setEmails(prev => prev.map(e => e.id === emailId ? { ...e, is_starred: isStarred } : e));
      if (selectedEmail?.id === emailId) {
        setSelectedEmail({ ...selectedEmail, is_starred: isStarred });
      }
      fetchStats(activeAccount);
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleDeleteEmail = async (emailId) => {
    try {
      const config = activeAccount ? { headers: { 'X-Account-Email': activeAccount } } : {};
      await axios.delete(`${API}/emails/${emailId}`, config);
      setEmails(prev => prev.filter(e => e.id !== emailId));
      if (selectedEmail?.id === emailId) {
        setSelectedEmail(null);
        setMobileView('list');
      }
      fetchFolders(activeAccount);
      fetchStats(activeAccount);
      toast.success('Email supprimé');
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleMarkAsRead = async (emailId) => {
    try {
      const config = activeAccount ? { headers: { 'X-Account-Email': activeAccount } } : {};
      await axios.patch(`${API}/emails/${emailId}`, { is_read: true }, config);
      setEmails(prev => prev.map(e => e.id === emailId ? { ...e, is_read: true } : e));
      if (selectedEmail?.id === emailId) {
        setSelectedEmail({ ...selectedEmail, is_read: true });
      }
      fetchFolders(activeAccount);
      fetchStats(activeAccount);
    } catch (error) {
      toast.error('Erreur lors du marquage');
    }
  };

  const findFolderForBulk = (kind) => {
    const matchers = {
      archive: ['archive', 'archives'],
      spam: ['spam', 'junk', 'indésirable', 'courrier indésirable'],
      trash: ['trash', 'deleted', 'corbeille', 'supprimés', 'éléments supprimés'],
    };
    return folders.find((folder) => {
      const id = (folder.id || '').toLowerCase();
      const name = (folder.name || '').toLowerCase();
      return matchers[kind].some((needle) => id.includes(needle) || name.includes(needle));
    });
  };

  const applyBulkListState = (ids, updater) => {
    const selected = new Set(ids);
    const apply = (list) => list.map((email) => selected.has(email.id) ? updater(email) : email);
    setEmails((prev) => apply(prev));
    setSearchResults((prev) => apply(prev));
    pageCacheRef.current.forEach((value) => {
      if (value?.emails) value.emails = apply(value.emails);
    });
  };

  const removeBulkFromLists = (ids) => {
    const selected = new Set(ids);
    const apply = (list) => list.filter((email) => !selected.has(email.id));
    setEmails((prev) => apply(prev));
    setSearchResults((prev) => apply(prev));
    pageCacheRef.current.forEach((value) => {
      if (value?.emails) value.emails = apply(value.emails);
    });
  };

  const handleBulkUpdate = async (ids, payload, { successMessage, removeFromList = false, updater = null } = {}) => {
    const selected = [...ids];
    const previousEmails = emails;
    const previousSearchResults = searchResults;
    const previousCache = new Map(pageCacheRef.current);
    const config = activeAccount ? { headers: { 'X-Account-Email': activeAccount } } : {};
    try {
      const { data } = await axios.patch(`${API}/emails/bulk`, { ids: selected, ...payload }, config);
      const failed = new Set(data?.failed || []);
      const succeeded = selected.filter((id) => !failed.has(id));

      if (removeFromList) removeBulkFromLists(succeeded);
      else if (updater) applyBulkListState(succeeded, updater);

      await fetchFolders(activeAccount);
      await fetchStats(activeAccount);

      if (failed.size > 0) toast.error(`${failed.size} email(s) non mis à jour. ${succeeded.length} réussi(s).`);
      else toast.success(successMessage || `${succeeded.length} email(s) mis à jour`);
    } catch (error) {
      setEmails(previousEmails);
      setSearchResults(previousSearchResults);
      pageCacheRef.current = previousCache;
      toast.error('Action groupée impossible. Réessayez dans quelques secondes.');
    }
  };

  const handleBulkDelete = async (ids) => {
    const toDelete = [...ids];
    const trash = findFolderForBulk('trash');
    if (trash) {
      await handleBulkUpdate(toDelete, { folder_id: trash.id }, {
        removeFromList: true,
        successMessage: `${toDelete.length} email${toDelete.length > 1 ? 's' : ''} déplacé${toDelete.length > 1 ? 's' : ''} dans la corbeille`,
      });
      return;
    }
    setEmails(prev => prev.filter(e => !toDelete.includes(e.id)));
    if (selectedEmail && toDelete.includes(selectedEmail.id)) {
      setSelectedEmail(null);
      setMobileView('list');
    }
    let errors = 0;
    const config = activeAccount ? { headers: { 'X-Account-Email': activeAccount } } : {};
    await Promise.all(toDelete.map(id =>
      axios.delete(`${API}/emails/${id}`, config).catch(() => { errors++; })
    ));
    fetchFolders(activeAccount);
    fetchStats(activeAccount);
    if (errors > 0) toast.error(`${errors} email(s) non supprimé(s)`);
    else toast.success(`${toDelete.length} email${toDelete.length > 1 ? 's' : ''} supprimé${toDelete.length > 1 ? 's' : ''}`);
  };

  const handleBulkMarkRead = async (ids) => {
    const count = [...ids].length;
    await handleBulkUpdate(ids, { is_read: true }, {
      updater: (email) => ({ ...email, is_read: true }),
      successMessage: `${count} email${count > 1 ? 's' : ''} marqué${count > 1 ? 's' : ''} comme lu`,
    });
  };

  const handleBulkMarkUnread = async (ids) => {
    const count = [...ids].length;
    await handleBulkUpdate(ids, { is_read: false }, {
      updater: (email) => ({ ...email, is_read: false }),
      successMessage: `${count} email${count > 1 ? 's' : ''} marqué${count > 1 ? 's' : ''} comme non lu`,
    });
  };

  const handleBulkStar = async (ids) => {
    const count = [...ids].length;
    await handleBulkUpdate(ids, { is_starred: true }, {
      updater: (email) => ({ ...email, is_starred: true }),
      successMessage: `${count} email${count > 1 ? 's' : ''} ajouté${count > 1 ? 's' : ''} aux favoris`,
    });
  };

  const handleBulkImportant = async (ids) => {
    const count = [...ids].length;
    await handleBulkUpdate(ids, { is_important: true }, {
      updater: (email) => ({ ...email, is_important: true }),
      successMessage: `${count} email${count > 1 ? 's' : ''} marqué${count > 1 ? 's' : ''} comme important`,
    });
  };

  const handleBulkArchive = async (ids) => {
    const archive = findFolderForBulk('archive');
    if (!archive) {
      toast.error('Dossier archive introuvable');
      return;
    }
    await handleBulkUpdate(ids, { folder_id: archive.id }, {
      removeFromList: true,
      successMessage: `${[...ids].length} email(s) archivé(s)`,
    });
  };

  const handleBulkSpam = async (ids) => {
    const spam = findFolderForBulk('spam');
    if (!spam) {
      toast.error('Dossier spam introuvable');
      return;
    }
    await handleBulkUpdate(ids, { folder_id: spam.id }, {
      removeFromList: true,
      successMessage: `${[...ids].length} email(s) déplacé(s) en spam`,
    });
  };

  const handleMoveEmail = async (emailId, folderId) => {
    try {
      const config = activeAccount ? { headers: { 'X-Account-Email': activeAccount } } : {};
      await axios.patch(`${API}/emails/${emailId}`, { folder_id: folderId }, config);
      setEmails(prev => prev.filter(e => e.id !== emailId));
      fetchFolders(activeAccount);
      toast.success('Email déplacé');
    } catch (error) {
      toast.error('Erreur lors du déplacement');
    }
  };

  const handleSearch = async (query, execute = false) => {
    setSearchQuery(query);
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
      searchDebounceRef.current = null;
    }

    const trimmedQuery = query.trim();
    if (!trimmedQuery) {
      setSearchResults([]);
      setIsSearching(false);
      return;
    }

    if (!execute && trimmedQuery.length < 3) return;

    const runSearch = async () => {
      setIsSearching(true);
      setLoading(true);
      try {
        const config = activeAccount ? { headers: { 'X-Account-Email': activeAccount } } : {};
        const response = await axios.post(`${API}/emails/search`, { query: trimmedQuery }, config);
        setSearchResults(response.data);
        setCurrentView('search');
      } catch (error) {
        toast.error('Erreur lors de la recherche');
      } finally {
        setLoading(false);
      }
    };

    if (execute) {
      await runSearch();
    } else {
      searchDebounceRef.current = setTimeout(runSearch, 300);
    }
  };

  const handleCompose = (data = null) => {
    setComposeData(data);
    setIsComposeOpen(true);
  };

  const handleSendEmail = async (emailData, sendAsEmail = null) => {
    try {
      const config = sendAsEmail ? { headers: { 'X-Account-Email': sendAsEmail } } : {};
      await axios.post(`${API}/emails`, emailData, config);
      toast.success('Email envoyé!');
      setIsComposeOpen(false);
      fetchFolders(activeAccount);
      fetchStats(activeAccount);
    } catch (error) {
      toast.error('Erreur lors de l\'envoi');
      throw error;
    }
  };

  const handleSaveDraft = async (emailData, sendAsEmail = null) => {
    try {
      const config = sendAsEmail ? { headers: { 'X-Account-Email': sendAsEmail } } : {};
      await axios.post(`${API}/emails/draft`, emailData, config);
      toast.success('Brouillon enregistré');
      setIsComposeOpen(false);
      fetchFolders(activeAccount);
    } catch (error) {
      toast.error('Erreur lors de l\'enregistrement');
    }
  };

  const handleViewChange = (view) => {
    setCurrentView(view);
    setSelectedEmail(null);
    setMobileView('list');
    setIsMobileSidebarOpen(false);
  };

  const renderMainContent = () => {
    if (currentView === 'contacts') {
      return <ContactsView />;
    }
    
    if (currentView === 'settings') {
      return <SettingsView />;
    }
    
    // Layout Gmail : liste plein écran → clic → mail plein écran
    if (selectedEmail) {
      const visibleEmails = currentView === 'search' ? searchResults : emails;
      const currentIdx = visibleEmails.findIndex(e => e.id === selectedEmail.id);
      const backToCurrentList = () => {
        if (currentView === 'search') {
          setSelectedEmail(null);
          setMobileView('list');
        } else {
          handleBackToList();
        }
      };
      return (
        <EmailView
          email={selectedEmail}
          folders={folders}
          labels={labels}
          onStar={handleStarEmail}
          onDelete={(id) => { handleDeleteEmail(id); backToCurrentList(); }}
          onMove={handleMoveEmail}
          onReply={() => handleCompose({ replyTo: selectedEmail, replyFromAccount: activeAccount })}
          onReplyAll={() => handleCompose({ replyTo: selectedEmail, replyAll: true, replyFromAccount: activeAccount })}
          onForward={() => handleCompose({ forward: selectedEmail, replyFromAccount: activeAccount })}
          onBack={backToCurrentList}
          onPrev={currentIdx > 0 ? () => handleEmailSelect(visibleEmails[currentIdx - 1]) : null}
          onNext={currentIdx < visibleEmails.length - 1 ? () => handleEmailSelect(visibleEmails[currentIdx + 1]) : null}
          currentIndex={currentIdx >= 0 ? currentIdx + (currentView === 'search' ? 1 : (currentPage - 1) * pageSize) : null}
          totalCount={currentView === 'search' ? visibleEmails.length : totalCount}
          isMobile={false}
        />
      );
    }

    if (currentView === 'search' && isSearching) {
      return (
        <EmailList
          emails={searchResults}
          loading={loading}
          selectedEmail={null}
          onEmailSelect={handleEmailSelect}
          onStarEmail={handleStarEmail}
          onDeleteEmail={handleDeleteEmail}
          onMarkAsRead={handleMarkAsRead}
          onBulkDelete={handleBulkDelete}
          onBulkMarkRead={handleBulkMarkRead}
          onBulkMarkUnread={handleBulkMarkUnread}
          onBulkStar={handleBulkStar}
          onBulkImportant={handleBulkImportant}
          onBulkArchive={handleBulkArchive}
          onBulkSpam={handleBulkSpam}
          onRefresh={() => handleSearch(searchQuery, true)}
          currentView="search"
          folderName={`Recherche: "${searchQuery}"`}
          currentPage={1}
          totalPages={1}
          totalCount={searchResults.length}
          pageSize={searchResults.length || pageSize}
          onPageChange={() => {}}
          listRef={listContainerRef}
        />
      );
    }

    return (
      <EmailList
        emails={emails}
        loading={loading}
        selectedEmail={null}
        onEmailSelect={handleEmailSelect}
        onStarEmail={handleStarEmail}
        onDeleteEmail={handleDeleteEmail}
        onMarkAsRead={handleMarkAsRead}
        onBulkDelete={handleBulkDelete}
        onBulkMarkRead={handleBulkMarkRead}
        onBulkMarkUnread={handleBulkMarkUnread}
        onBulkStar={handleBulkStar}
        onBulkImportant={handleBulkImportant}
        onBulkArchive={handleBulkArchive}
        onBulkSpam={handleBulkSpam}
        onRefresh={handleRefresh}
        currentView={currentView}
        folderName={selectedFolder?.name || 'Boîte de réception'}
        currentPage={currentPage}
        totalPages={Math.ceil(totalCount / pageSize)}
        totalCount={totalCount}
        pageSize={pageSize}
        onPageChange={handlePageChange}
        listRef={listContainerRef}
      />
    );
  };

  return (
    <div className="flex h-screen overflow-hidden" data-testid="dashboard">
      <Sidebar
        user={user}
        folders={folders}
        labels={labels}
        selectedFolder={selectedFolder}
        currentView={currentView}
        stats={stats}
        accountsStats={accountsStats}
        onFolderSelect={handleFolderSelect}
        onViewChange={handleViewChange}
        onCompose={() => handleCompose()}
        onSearch={handleSearch}
        searchQuery={searchQuery}
        isOpen={isMobileSidebarOpen}
        onClose={() => setIsMobileSidebarOpen(false)}
      />
      
      <main className="flex-1 flex flex-col h-screen overflow-hidden bg-background">
        <div className="hidden md:flex items-center gap-3 px-4 py-3 border-b border-border bg-card">
          <div className="relative w-full max-w-2xl">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <input
              type="text"
              placeholder="Rechercher dans les emails..."
              className="w-full h-10 pl-10 pr-4 rounded-xl bg-muted border border-border text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
              value={searchQuery}
              onChange={(e) => handleSearch(e.target.value, false)}
              onKeyDown={(e) => { if (e.key === 'Enter' && searchQuery.trim()) handleSearch(searchQuery, true); }}
              data-testid="desktop-search-input"
            />
          </div>
        </div>

        {/* Mobile header - Gmail style */}
        <div className="md:hidden flex flex-col border-b border-border bg-card safe-area-top">
          {mobileView === 'detail' ? null : (
            <>
              {/* Ligne 1 : Barre de recherche pleine largeur avec burger dedans */}
              <div className="relative px-3 pt-3 pb-3">
                <Search className="absolute left-6 top-1/2 -translate-y-1/2 w-4 h-4 text-primary-foreground/70 z-10" />
                <input
                  type="text"
                  placeholder="Rechercher dans les emails..."
                  className="w-full h-11 pl-10 pr-14 rounded-full bg-primary border border-primary text-sm text-primary-foreground placeholder:text-primary-foreground/60 focus:outline-none focus:ring-2 focus:ring-primary/80 focus:ring-offset-2 focus:ring-offset-card"
                  value={searchQuery}
                  onChange={(e) => handleSearch(e.target.value, false)}
                  onKeyDown={(e) => { if (e.key === 'Enter' && searchQuery.trim()) handleSearch(searchQuery, true); }}
                  data-testid="mobile-search-input"
                />
                <button
                  onClick={() => setIsMobileSidebarOpen(true)}
                  className="absolute right-6 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center rounded-full bg-primary-foreground/15 hover:bg-primary-foreground/25 touch-manipulation transition-colors"
                  data-testid="mobile-menu-btn"
                >
                  <Menu className="w-5 h-5 text-primary-foreground" />
                </button>
              </div>
              {/* Ligne 2 : Logo + dropdown compte à gauche, refresh + sélecteur dossier à droite */}
              <div className="flex items-center justify-between px-3 pb-3">
                <div className="flex items-center gap-2 min-w-0 flex-1">
                  <img src="/logo.png" alt="RenoviaMail" className="w-10 h-10 flex-shrink-0" />
                  <div className="relative flex-1 min-w-0">
                    <button
                      onClick={() => { setShowAccountDropdown(!showAccountDropdown); setShowFolderDropdown(false); }}
                      className="flex items-center gap-1 w-full min-w-0 rounded-lg py-1.5 px-2 text-left text-sm text-muted-foreground hover:bg-accent/50 touch-manipulation"
                      data-testid="account-dropdown-btn"
                    >
                      <span className="truncate">{accounts.find(a => (a.is_default && !activeAccount) || a.email === activeAccount)?.label || activeAccount || user?.email}</span>
                      <ChevronDown className={cn("w-3.5 h-3.5 flex-shrink-0 transition-transform", showAccountDropdown && "rotate-180")} />
                    </button>
                    {showAccountDropdown && accounts.length > 0 && (
                      <>
                        <div className="fixed inset-0 z-40" onClick={() => setShowAccountDropdown(false)} />
                        <div className="absolute left-0 top-full mt-1 bg-card border border-border rounded-xl shadow-xl z-50 py-1 min-w-[200px] max-w-[calc(100vw-8rem)] animate-fade-in">
                          {accounts.map((acct) => (
                            <button
                              key={acct.id}
                              onClick={() => {
                                setActiveAccount(acct.is_default ? null : acct.email);
                                setShowAccountDropdown(false);
                              }}
                              className={cn(
                                "w-full text-left px-4 py-2.5 text-sm flex items-center gap-2 touch-manipulation hover:bg-accent transition-colors",
                                ((!activeAccount && acct.is_default) || activeAccount === acct.email) && "bg-primary/10 text-primary font-semibold"
                              )}
                            >
                              <span className="w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center text-xs font-bold flex-shrink-0">{(acct.label || acct.email)[0].toUpperCase()}</span>
                              <div className="min-w-0 flex-1">
                                <p className="truncate font-medium">{acct.label}</p>
                                <p className="truncate text-xs text-muted-foreground">{acct.email}</p>
                              </div>
                            </button>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <button
                    onClick={handleRefresh}
                    disabled={refreshing}
                    className="flex items-center justify-center w-8 h-8 rounded-full bg-primary/10 text-primary touch-manipulation transition-colors hover:bg-primary/20 disabled:opacity-50"
                    data-testid="refresh-btn"
                  >
                    <RefreshCw className={cn("w-4 h-4", refreshing && "animate-spin")} />
                  </button>
                <div className="relative flex-shrink-0">
                  <button
                    onClick={() => { setShowFolderDropdown(!showFolderDropdown); setShowAccountDropdown(false); }}
                    className="flex items-center gap-1 px-2.5 py-1 rounded-full bg-primary/10 text-primary text-xs font-semibold touch-manipulation"
                    data-testid="folder-dropdown-btn"
                  >
                    <span>{selectedFolder?.name || 'Boîte de réception'}</span>
                    <ChevronDown className={cn("w-3.5 h-3.5 transition-transform", showFolderDropdown && "rotate-180")} />
                  </button>
                  {showFolderDropdown && (
                    <>
                      <div className="fixed inset-0 z-40" onClick={() => setShowFolderDropdown(false)} />
                      <div className="absolute top-full right-0 mt-1 bg-card border border-border rounded-xl shadow-xl z-50 py-1 min-w-[200px] animate-fade-in">
                        {folders.map((folder) => (
                          <button
                            key={folder.id}
                            onClick={() => handleFolderSelect(folder)}
                            className={cn(
                              "w-full text-left px-4 py-2.5 text-sm flex items-center justify-between touch-manipulation hover:bg-accent transition-colors",
                              selectedFolder?.id === folder.id && "text-primary font-semibold bg-primary/5"
                            )}
                          >
                            <span>{folder.name}</span>
                            {folder.unread_count > 0 && (
                              <span className="text-xs bg-primary/20 text-primary px-2 py-0.5 rounded-full font-medium">
                                {folder.unread_count}
                              </span>
                            )}
                          </button>
                        ))}
                      </div>
                    </>
                  )}
                </div>
                </div>
              </div>
            </>
          )}
        </div>
        
        <div className="flex-1 overflow-hidden">
          {renderMainContent()}
        </div>
      </main>

      <ComposeModal
        isOpen={isComposeOpen}
        onClose={() => setIsComposeOpen(false)}
        onSend={handleSendEmail}
        onSaveDraft={handleSaveDraft}
        initialData={composeData}
        userEmail={user?.email}
        userName={user?.full_name}
        accounts={accounts}
        activeAccount={activeAccount}
      />

      {/* FAB - Nouveau message */}
      {(currentView === 'inbox' || currentView === 'starred' || currentView === 'important') && mobileView === 'list' && (
        <button
          onClick={() => handleCompose()}
          className="md:hidden fab-compose bg-primary text-primary-foreground"
          data-testid="fab-compose-btn"
        >
          <Pencil className="w-5 h-5 mr-2" />
          <span className="text-sm font-semibold">Nouveau message</span>
        </button>
      )}
    </div>
  );
};

export default DashboardPage;
